import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Info, Plus, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  agentFunctionsApi,
  type AgentFunction,
  type FunctionLog,
} from "@/features/agents/api";
import { cn } from "@/lib/utils";

const KEY = ["agent-functions"] as const;

const STATUS_COLOR: Record<string, string> = {
  ok: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400",
  ssrf_blocked:
    "text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400",
  error: "text-rose-600 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-400",
};

function StatusChip({ status }: { status: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase",
        STATUS_COLOR[status] ?? "text-muted-foreground bg-muted",
      )}
    >
      {status}
    </span>
  );
}

export default function AgentFunctionsTab({
  agentId,
  canEdit,
}: {
  agentId: string;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: [...KEY, "list", agentId],
    queryFn: () => agentFunctionsApi.list(agentId),
    staleTime: 30_000,
  });
  const functions = q.data ?? [];

  const [addOpen, setAddOpen] = useState(false);
  const [execFn, setExecFn] = useState<AgentFunction | null>(null);
  const [deleteFn, setDeleteFn] = useState<AgentFunction | null>(null);

  const remove = useMutation({
    mutationFn: (id: string) => agentFunctionsApi.remove(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [...KEY, "list", agentId] }),
  });

  async function confirmDelete() {
    if (!deleteFn) return;
    try {
      await remove.mutateAsync(deleteFn.id);
      toast.success("Function deleted");
    } catch {
      toast.error("Failed to delete function");
    }
    setDeleteFn(null);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Functions (Tools)</h2>
        {canEdit && (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="size-4" />
            Add Function
          </Button>
        )}
      </div>

      {q.isLoading && (
        <Card>
          <CardContent className="space-y-2 p-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </CardContent>
        </Card>
      )}

      {!q.isLoading && functions.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            No functions defined. Add a webhook function to let the agent call
            external APIs during conversations.
          </CardContent>
        </Card>
      )}

      {!q.isLoading &&
        functions.map((fn) => (
          <FunctionAccordion
            key={fn.id}
            fn={fn}
            canEdit={canEdit}
            onExec={() => setExecFn(fn)}
            onDelete={() => setDeleteFn(fn)}
          />
        ))}

      <AddFunctionDialog
        agentId={agentId}
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={() =>
          qc.invalidateQueries({ queryKey: [...KEY, "list", agentId] })
        }
      />

      <ExecuteDialog fn={execFn} onClose={() => setExecFn(null)} />

      <Dialog
        open={!!deleteFn}
        onOpenChange={(v) => !v && setDeleteFn(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete function</DialogTitle>
            <DialogDescription>
              Delete{" "}
              <span className="font-medium text-foreground">
                {deleteFn?.name}
              </span>
              ? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFn(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={remove.isPending}
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FunctionAccordion({
  fn,
  canEdit,
  onExec,
  onDelete,
}: {
  fn: AgentFunction;
  canEdit: boolean;
  onExec: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const logsQ = useQuery({
    queryKey: ["agent-functions", "logs", fn.id],
    queryFn: () => agentFunctionsApi.logs(fn.id),
    enabled: open,
    staleTime: 15_000,
  });

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
        <div className="flex-1">
          <p className="font-semibold">{fn.name}</p>
          {fn.description && (
            <p className="text-xs text-muted-foreground">{fn.description}</p>
          )}
        </div>
        <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs">
          {fn.execution_type}
        </span>
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
            fn.is_active
              ? "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400"
              : "text-muted-foreground bg-muted",
          )}
        >
          {fn.is_active ? "active" : "inactive"}
        </span>
      </button>

      {open && (
        <div className="border-t px-4 py-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <JsonBlock title="Execution Config" value={fn.execution_config} />
            <JsonBlock title="Parameters Schema" value={fn.parameters_schema} />
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onExec}>
              <Zap className="size-4" />
              Test Execute
            </Button>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={onDelete}
              >
                <Trash2 className="size-4" />
                Delete
              </Button>
            )}
          </div>

          <div>
            <p className="mb-2 text-sm font-medium">Recent Executions</p>
            <LogsTable logs={logsQ.data} loading={logsQ.isLoading} />
          </div>
        </div>
      )}
    </Card>
  );
}

function JsonBlock({
  title,
  value,
}: {
  title: string;
  value: Record<string, unknown>;
}) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      <pre className="max-h-40 overflow-auto rounded-md bg-muted/50 p-2 text-[11px] font-mono">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

function LogsTable({
  logs,
  loading,
}: {
  logs: FunctionLog[] | undefined;
  loading: boolean;
}) {
  if (loading) return <Skeleton className="h-16 w-full" />;
  if (!logs || logs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No executions yet.</p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-xs">
        <thead className="border-b text-left text-[10px] uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">Time</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Duration</th>
            <th className="px-3 py-2 font-medium">HTTP</th>
            <th className="px-3 py-2 font-medium">Error</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {logs.slice(0, 10).map((log) => (
            <tr key={log.id}>
              <td className="px-3 py-1.5 text-muted-foreground">
                {new Date(log.created_at).toLocaleString()}
              </td>
              <td className="px-3 py-1.5">
                <StatusChip status={log.status} />
              </td>
              <td className="px-3 py-1.5 tabular-nums">{log.duration_ms}ms</td>
              <td className="px-3 py-1.5 tabular-nums">
                {log.response_code ?? "—"}
              </td>
              <td
                className="max-w-[200px] truncate px-3 py-1.5 text-rose-600 dark:text-rose-400"
                title={log.error_detail ?? ""}
              >
                {log.error_detail || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Real webhook execution_config — mirrors what executor.py actually reads.
const WEBHOOK_TEMPLATE = JSON.stringify(
  {
    url: "https://your-endpoint.com/hook",
    method: "POST",
    headers: {},
    signing_secret: "",
    timeout_seconds: 10,
  },
  null,
  2,
);

const SCHEMA_EXAMPLE = JSON.stringify(
  {
    type: "object",
    properties: {
      order_id: { type: "string", description: "The order ID to look up" },
    },
    required: ["order_id"],
  },
  null,
  2,
);

// Per-field reference for the webhook config, kept in sync with executor.py.
const WEBHOOK_FIELDS: Array<{ key: string; req: boolean; desc: string }> = [
  { key: "url", req: true, desc: "HTTPS endpoint we POST to. Localhost/private URLs are blocked (SSRF guard)." },
  { key: "method", req: false, desc: "HTTP method — POST (default), GET or PUT." },
  { key: "headers", req: false, desc: 'Extra request headers, e.g. {"Authorization": "Bearer …"}.' },
  { key: "signing_secret", req: false, desc: "If set, we add an HMAC X-Chawowa-Tool-Signature header so your server can verify the request." },
  { key: "timeout_seconds", req: false, desc: "Max seconds to wait for a response (default 10)." },
];

function parseJson(text: string): { value: unknown; ok: boolean } {
  const t = text.trim();
  if (t === "") return { value: {}, ok: true };
  try {
    return { value: JSON.parse(t), ok: true };
  } catch {
    return { value: null, ok: false };
  }
}

function AddFunctionDialog({
  agentId,
  open,
  onOpenChange,
  onCreated,
}: {
  agentId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [executionType, setExecutionType] = useState("webhook");
  const [executionConfig, setExecutionConfig] = useState(WEBHOOK_TEMPLATE);
  const [parametersSchema, setParametersSchema] = useState("{}");
  const [showFields, setShowFields] = useState(false);
  const [showSchemaExample, setShowSchemaExample] = useState(false);

  const isWebhook = executionType === "webhook";
  const isIntegration = executionType === "integration";

  // ── Validation ──────────────────────────────────────────────────────────
  const cfg = parseJson(executionConfig);
  const schema = parseJson(parametersSchema);

  let configError: string | null = null;
  if (isWebhook) {
    if (!cfg.ok) {
      configError = "Execution Config is not valid JSON.";
    } else {
      const url = (cfg.value as { url?: unknown })?.url;
      if (typeof url !== "string" || !url.trim()) {
        configError = 'Webhook needs a "url".';
      }
    }
  }
  const schemaError = schema.ok ? null : "Parameters Schema is not valid JSON.";

  const create = useMutation({
    mutationFn: () =>
      agentFunctionsApi.create({
        agent: agentId,
        name: name.trim(),
        description: description.trim(),
        execution_type: executionType,
        execution_config: cfg.value as Record<string, unknown>,
        parameters_schema: schema.value as Record<string, unknown>,
      }),
  });

  const canSubmit =
    !!name.trim() &&
    isWebhook &&
    !configError &&
    !schemaError &&
    !create.isPending;

  function reset() {
    setName("");
    setDescription("");
    setExecutionType("webhook");
    setExecutionConfig(WEBHOOK_TEMPLATE);
    setParametersSchema("{}");
    setShowFields(false);
    setShowSchemaExample(false);
  }

  function handleTypeChange(v: string) {
    setExecutionType(v);
    // Pre-fill the real template when the user picks Webhook.
    if (v === "webhook") setExecutionConfig(WEBHOOK_TEMPLATE);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      await create.mutateAsync();
      toast.success("Function added");
      reset();
      onOpenChange(false);
      onCreated();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error || "Failed to add function");
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Function</DialogTitle>
          <DialogDescription>
            Define a tool the agent can call during conversation.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="fn-name">Function Name</Label>
            <Input
              id="fn-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="lookup_order"
            />
            <p className="text-xs text-muted-foreground">
              Used in LLM function-call schema (no spaces, snake_case).
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fn-desc">Description</Label>
            <Input
              id="fn-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What the function does (shown to the LLM)"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Execution Type</Label>
            <Select value={executionType} onValueChange={handleTypeChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="webhook">Webhook — call your own HTTP API</SelectItem>
                <SelectItem value="integration">Integration — Cal.com, HubSpot, Odoo…</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isIntegration ? (
            <div className="flex gap-2 rounded-md border border-amber-300/60 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300">
              <Info className="size-4 shrink-0" />
              <p>
                Integration tools (Cal.com, HubSpot, Odoo…) are added{" "}
                <span className="font-medium">automatically</span> when you
                connect the integration on the{" "}
                <span className="font-medium">Integrations</span> page. You
                don&apos;t configure them here.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="fn-exec">Execution Config (JSON)</Label>
                <Textarea
                  id="fn-exec"
                  rows={7}
                  value={executionConfig}
                  onChange={(e) => setExecutionConfig(e.target.value)}
                  className="font-mono text-xs"
                />
                {configError && (
                  <p className="text-xs font-medium text-destructive">
                    {configError}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => setShowFields((v) => !v)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  <ChevronRight
                    className={cn(
                      "size-3.5 transition-transform",
                      showFields && "rotate-90",
                    )}
                  />
                  Field reference
                </button>
                {showFields && (
                  <ul className="space-y-1 rounded-md bg-muted/50 p-2 text-xs">
                    {WEBHOOK_FIELDS.map((f) => (
                      <li key={f.key}>
                        <code className="font-mono">{f.key}</code>{" "}
                        <span
                          className={cn(
                            "text-[10px] uppercase",
                            f.req
                              ? "text-destructive"
                              : "text-muted-foreground",
                          )}
                        >
                          {f.req ? "required" : "optional"}
                        </span>
                        <span className="text-muted-foreground">
                          {" "}
                          — {f.desc}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="fn-schema">Parameters Schema (JSON Schema)</Label>
            <Textarea
              id="fn-schema"
              rows={3}
              value={parametersSchema}
              onChange={(e) => setParametersSchema(e.target.value)}
              className="font-mono text-xs"
            />
            {schemaError && (
              <p className="text-xs font-medium text-destructive">
                {schemaError}
              </p>
            )}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowSchemaExample((v) => !v)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                <ChevronRight
                  className={cn(
                    "size-3.5 transition-transform",
                    showSchemaExample && "rotate-90",
                  )}
                />
                Show example
              </button>
              {showSchemaExample && (
                <button
                  type="button"
                  onClick={() => setParametersSchema(SCHEMA_EXAMPLE)}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Use this example
                </button>
              )}
            </div>
            {showSchemaExample && (
              <pre className="overflow-auto rounded-md bg-muted/50 p-2 text-[11px] font-mono">
                {SCHEMA_EXAMPLE}
              </pre>
            )}
            <p className="text-xs text-muted-foreground">
              Describes the arguments the LLM should pass. Leave as{" "}
              <code className="font-mono">{"{}"}</code> if the function takes no
              arguments.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {create.isPending ? "Adding…" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ExecuteDialog({
  fn,
  onClose,
}: {
  fn: AgentFunction | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [args, setArgs] = useState("{}");
  const [result, setResult] = useState<{
    status: string;
    response_code?: number;
    response_body?: string;
    error_detail?: string;
    duration_ms?: number;
  } | null>(null);

  const exec = useMutation({
    mutationFn: () =>
      agentFunctionsApi.execute(fn!.id, JSON.parse(args || "{}")),
    onSuccess: (data) => {
      setResult(data);
      if (fn) {
        qc.invalidateQueries({ queryKey: ["agent-functions", "logs", fn.id] });
      }
    },
    onError: (err) => {
      const e = err as { response?: { data?: unknown } };
      setResult(
        (e?.response?.data as Record<string, unknown> | undefined) as {
          status: string;
        } ?? { status: "error", error_detail: "Execution failed" },
      );
    },
  });

  return (
    <Dialog
      open={!!fn}
      onOpenChange={(v) => {
        if (!v) {
          setResult(null);
          setArgs("{}");
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Test Execute — {fn?.name}</DialogTitle>
          <DialogDescription>
            The URL in execution_config is validated by the SSRF guard before
            calling.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="exec-args">Input Arguments (JSON)</Label>
            <Textarea
              id="exec-args"
              rows={5}
              value={args}
              onChange={(e) => setArgs(e.target.value)}
              className="font-mono text-xs"
            />
          </div>

          {result && (
            <div className="space-y-2">
              <StatusChip status={result.status} />
              {result.error_detail && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {result.error_detail}
                </div>
              )}
              {result.response_body && (
                <pre className="max-h-48 overflow-auto rounded-md bg-muted/50 p-2 text-xs font-mono whitespace-pre-wrap">
                  {result.response_body}
                </pre>
              )}
              <p className="text-xs text-muted-foreground">
                HTTP {result.response_code ?? "N/A"} · {result.duration_ms}ms
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => exec.mutate()} disabled={exec.isPending}>
            <Zap className="size-4" />
            {exec.isPending ? "Executing…" : "Execute"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
